import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { normalizeValue } from '@dte/otw-utils-core/src/strings/normalizeValue';
import { titleCaseValue } from '@dte/otw-utils-core/src/strings/titleCaseValue';
import { upperCaseValue } from '@dte/otw-utils-core/src/strings/upperCaseValue';
import { MessageIcon } from '../../../../interfaces/api/messageDetail/MessageIcon';
import type { PostalAddressDetails } from '../../../../interfaces/api/PostalAddressDetails';
import type { LocationDetails } from '../../../../interfaces/api/PremiseStatusV2';
import type { PremiseData, PremiseDataDocument } from '../../../../interfaces/containers/premises/PremiseData';
import { getAddressLine1 } from './getAddressLine1';
import { getAddressLine2 } from './getAddressLine2';

function getGPS(masterData: PremiseData) {
	// GeoJSON coordinates
	const coordinates = masterData?.gps?.coordinates;
	const longitude = coordinates?.[0];
	const latitude = coordinates?.[1];

	if (!latitude || !longitude) {
		return undefined;
	}

	return {
		latitude: latitude,
		longitude: longitude,
	};
}

export function getPremiseAddress(masterData: PremiseDataDocument | undefined): PostalAddressDetails | undefined {
	const data = masterData?.data;
	const address = data?.address;
	if (!address) {
		return undefined;
	}

	// TODO: should we return undefined if parts of the address are missing?
	const addressDetails: PostalAddressDetails = {
		line1: getAddressLine1(address) ?? '',
		line2: getAddressLine2(address),
		city: titleCaseValue(address.city) ?? '',
		state: upperCaseValue(address.state) ?? '',
		zipcode: normalizeValue(address.zip) ?? '',
	};

	return addressDetails;
}

export interface SingleLinePremiseAddressOptions {
	includeCity?: boolean;
	includeState?: boolean;
	includeZip?: boolean;
}

export function getSingleLinePremiseAddress(
	masterData: PremiseDataDocument | undefined,
	options: SingleLinePremiseAddressOptions = {},
): string | undefined {
	const { includeCity = true, includeState = true, includeZip = false } = options;

	const address = getPremiseAddress(masterData);
	if (!address) {
		return undefined;
	}

	let addressParts = [address.line1, address.line2];
	if (includeCity) {
		addressParts.push(address.city);
	}
	if (includeState) {
		addressParts.push(address.state);
	}

	addressParts = filterBlank(addressParts);
	let formattedAddress = addressParts.join(', ');

	if (includeZip) {
		const zip = address.zipcode?.substring(0, 5);
		if (zip) {
			formattedAddress += ` ${zip}`;
		}
	}

	return formattedAddress;
}

export function getPremiseLocation(masterData: PremiseDataDocument | undefined): LocationDetails | undefined {
	if (!masterData?.data) {
		return undefined;
	}

	const data = masterData.data;
	const address = data.address;
	if (!address) {
		return undefined;
	}

	const locationDetails: LocationDetails = {
		premiseId: masterData.premiseId,
		icon: MessageIcon.MapPin,
		gps: getGPS(data),
		address: getPremiseAddress(masterData),
	};

	return locationDetails;
}
